import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from 'frontend/admin/app/shared/components/base-component/base.component';
import { Subscription, of } from 'rxjs';
import { filter, mergeMap, take, tap } from 'rxjs/operators';
import { faTimes } from '@fortawesome/fontawesome-pro-light';
import { faLongArrowAltLeft } from '@fortawesome/pro-solid-svg-icons';
import { KnowledgeSummary } from 'frontend/admin/app/model/knowledge-summary.model';
import { WorkspaceQuery } from 'frontend/admin/app/pages/workspace/components/workspace/state/workspace.query';
import { CommonConstant } from 'frontend/admin/app/shared/constants/common.constant';
import { AppQuery } from 'frontend/admin/app/state/app.query';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { CreateQuotationModalQuery } from './state/create-quotation-modal.query';
import { CreateQuotationModalService } from './state/create-quotation-modal.service';
import { NotificationService } from 'frontend/admin/app/shared/services/notification.service';
import { LoadingService } from 'frontend/admin/app/shared/services/loading.service';
import { ELimitCharacters } from 'frontend/admin/app/enum/limit.characters.enum';
import { KnowledgeFirestoreService } from 'frontend/admin/app/shared/services/knowledge-firestore.service';
import { EKnowledgeType } from 'domain/kernel/knowledge/enum';

@Component({
  selector: 'app-create-quotation-modal',
  templateUrl: './create-quotation-modal.component.html',
  styleUrls: ['./create-quotation-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateQuotationModalComponent extends BaseComponent {

  readonly appData = {
    faTimes: faTimes,
    faLongArrowAltLeft: faLongArrowAltLeft,
    createBtn: '引用してナレッジを作成',
    updateBtn: '引用してナレッジを更新'
  }

  // for ngSelect
  ngSelectTags = {
    tags: [],// CategoryDto[] all categories belong to workspace
    selectedTags: [],// string[] all categoryIds have selected
    // initTags: [], // from ES
    categoryKnowledge: [],
    hideSelected: true,
    // addTag: this.addTagFn,
    multiple: true,
    // isOpen: false
  };

  // for attached files
  attachmentFiles = {
    inputFiles: [],
    remainFiles: [],
    files: [], // File[]
    fileSizeExceedsLimit: false
  };

  formGroup: FormGroup;
  subscription: Subscription;
  namePostOwner: string = null;
  inProcess: boolean = false;
  hiddenQuotation: boolean = true; // Hidden quotation by issue #705

  @Input() knowledgeSummaryEditor: KnowledgeSummary; // knowledge summary editor
  @Input() knowledgeSummary: KnowledgeSummary; // sharing-vendor quotation
  @Input() isCreateKnowledge: boolean = false;
  @Output() showList = new EventEmitter();

  public readonly ELimitCharacters = ELimitCharacters;

  constructor(
    readonly createQuotationModalQuery: CreateQuotationModalQuery,
    readonly appQuery: AppQuery,
    private readonly formBuilder: FormBuilder,
    private readonly workspaceQuery: WorkspaceQuery,
    private readonly createQuotationModalService: CreateQuotationModalService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly modelRef: NzModalRef<CreateQuotationModalComponent>,
    private readonly notificationService: NotificationService,
    private readonly _loadingService: LoadingService,
    private readonly knowledgeFirestoreService: KnowledgeFirestoreService
  ) {
    super();
    this.formGroup = this.formBuilder.group({
      title: ['', [Validators.required, Validators.maxLength(50)]],
      body: ['', [Validators.required, Validators.maxLength(ELimitCharacters.TEXTAREA_DEFAULT_MAX_LENGTH)]],
    });
  }

  protected override onDestroy(): void {
    this.createQuotationModalService.resetStore();
  }

  protected override onInit(): void {
    const currentAccountOrganization = this.appQuery.getValue().currentAccount;
    this.namePostOwner = `${currentAccountOrganization.name?.first} ${currentAccountOrganization.name?.last}`

    this.subscription = this.init().subscribe(rs => {
      if (rs && rs.length > 0) {
        this.attachmentFiles.inputFiles = this.attachmentFiles.remainFiles = rs;
      }
      // incase edit mode
      if (this.knowledgeSummaryEditor) {
        this.setFormValue(this.knowledgeSummaryEditor.title, this.knowledgeSummaryEditor.body);
        const tempArr = this.knowledgeSummaryEditor.categories ? this.knowledgeSummaryEditor.categories.split(',') : [];
        const initTags = this.ngSelectTags.tags.filter(tag => tempArr.includes(tag.value));

        this.ngSelectTags.selectedTags = initTags ? initTags.map(tag => tag.value) : []
      } else {
        this.setFormValue('', this.knowledgeSummary.body);
      }
      this.changeDetectorRef.detectChanges();
    });
  }

  setFormValue(title: string, body: string) {
    this.formGroup.setValue({
      title: title,
      body: body,
    });
  }

  close(data?: any) {
    this.modelRef.close(data);
  }

  closeValidate() {
    this.createQuotationModalService.updateFailed(false, []);
  }

  onSelectedTags(event: any) {
    this.ngSelectTags.selectedTags = event;
  }

  onUploadFiles($event: any): void {
    this.attachmentFiles.files = $event && $event.files;
    this.attachmentFiles.remainFiles = $event && $event.inputFiles;
    this.attachmentFiles.fileSizeExceedsLimit = $event && $event.fileSizeExceedsLimit;
  }

  onUpsertPost() {
    const validForm = this.validateCreateKnowledgeQuotation();
    if (!validForm.isValid) {
      this.createQuotationModalService.updateFailed(true, validForm.errMsgs);
      return;
    }
    if (this.ngSelectTags.selectedTags.length > CommonConstant.ITEM_TAG_MAXIMUM) {
      return;
    }

    this.inProcess = true;
    this._loadingService.setStateLoading(true);
    return this.knowledgeSummaryEditor ? this.update() : this.insert();
  }

  private insert() {
    const currentAccountOrganization = this.workspaceQuery.getValue().currentAccountOrganization;
    const subscription$ = this.knowledgeFirestoreService.create(
      currentAccountOrganization.id,
      EKnowledgeType.QUOTATION,
      this.formGroup.value,
      this.ngSelectTags.selectedTags,
      this.attachmentFiles.files,
      this.attachmentFiles.remainFiles,
      this.knowledgeSummary.id
    ).pipe(
      take(1),
      tap(knowledgeQuotation => this.closeModal(knowledgeQuotation)),
      tap(_ => {
        this.inProcess = false;
        this.showList.emit(this.knowledgeSummary);
        this._loadingService.setStateLoading(false);
      }),
    ).subscribe({
      next: () => {
        this.notificationService.open('ナレッジを作成しました。');
      },
      error: err => {
        console.error(err);
        this.inProcess = false;
      },
    });
    this.addSubscription(subscription$);
  }

  private update() {
    const currentAccountOrganization = this.workspaceQuery.getValue().currentAccountOrganization;
    const inputFileIds = this.attachmentFiles.inputFiles.map(file => file.id);
    const remainFileIds = this.attachmentFiles.remainFiles.map(file => file.id)
    const deleteFiles = inputFileIds.filter(n => !remainFileIds.includes(n));
    const addedFiles = this.attachmentFiles.files;

    const subscription$ = this.knowledgeFirestoreService.update(
      currentAccountOrganization.id,
      EKnowledgeType.QUOTATION,
      this.knowledgeSummaryEditor.id,
      this.formGroup.value,
      this.ngSelectTags.selectedTags,
      deleteFiles,
      addedFiles
    ).pipe(
      take(1),
      tap(knowledge => this.close(knowledge)),
      tap(_ => {
        this.inProcess = false;
        this.showList.emit(this.knowledgeSummary);
        this._loadingService.setStateLoading(false);
      }),
    ).subscribe({
      error: err => { }
    });
    this.addSubscription(subscription$);
  }

  private init() {
    return this.workspaceQuery.selectOrganization().pipe(
      filter(organization => organization !== null),
      mergeMap(organization => this.knowledgeFirestoreService.getCategoryByOrganizationId(organization.id)),
      tap(result => (this.ngSelectTags.tags = result.map(x => ({ value: x.id, label: x.label } as any)))),
      mergeMap(_ => this.knowledgeSummaryEditor ? this.knowledgeFirestoreService.getFilesByKnowledgeId(this.knowledgeSummaryEditor.id) : of(null)),
    );
  }

  private validateCreateKnowledgeQuotation() {
    const rs = { isValid: true, errMsgs: [] };
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      rs.isValid = false;
      rs.errMsgs.push('必要項目に入力してください!');
    }
    return rs;
  }

  private closeModal(data?: any): void {
    this.modelRef.close(data);
    // if (this.modelRef) {
    //   this.modelRef.close(data);
    // }
  }
}
